/**
 * @author omriha
 * 01/10/2019
 */
import React, {useEffect} from "react";
import {connect, useDispatch} from "react-redux";
import {push} from 'connected-react-router';
import {useTranslation} from 'react-i18next';
import queryString from 'query-string';
import SMCPage from '../../../components/core/pageComponents/SMCPage';
import ButtonContainer from '../../../components/Simple/ButtonContainer';
import DropdownContainer from '../../../components/core/dropdown/DropdownContainer';
import DropdownRow from "../../../components/core/dropdown/DropdownRow";
import {useBoolean, useEditorDimensions} from '../../../utils/hooks';
import LabelContainer from '../../../components/Page/LabelContainer';
import SMCEditor from '../../../components/Editor';
import {SMCPaths} from '../../../conf/Routes';
import SMCButton from '../../../components/Simple/SMCButton';
import {useGatewayEditor} from './GatwayEditorHelper';
import {seamlessLoginIcon} from '../../../assets/images/buttons';
import {SMCDisabledProvider} from '../../../components/Simple/SMCDisabledContext';
import {gatewayPermissionsSelector} from '../../../store/selectors/user.selectors';
import RouteLeavingGuard from '../../PopupPages/RouteLeavingGuard';
import GatewayWizard from '../../Wizards/GatewayWizard';
import {GatewayGetSchema} from '../../../api/DBSchema/Gateway';
import VerifyButtonsContainer from '../VerifyButtonsContainer'
import lineSeparator from '../../../assets/images/Line.png';
import {setShowBackButton} from "../../../store/actions/view.actions";
import ButtonsContainerStyles from "../ButtonsContainerStyles";

/**
 * High level element to fetch, edit and save gateway, all logic implemented inside.
 * @param {string} id Id of gateway to fetch from the server to edit
 */
function GatewayEditor({ navigateTo, id, urlParams, gatewayPermissions }) {
  const { t } = useTranslation();
  const { editorWidth, editorHeight } = useEditorDimensions();
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(setShowBackButton(true,t("SMC_GATEWAY_EDITOR_PAGENAME")));
    return () => {
      dispatch(setShowBackButton(false, ""));
    };
  }, [dispatch, t]);

  const modifyPermission = gatewayPermissions
    ? gatewayPermissions.modify
    : true;
  const deletePermission = gatewayPermissions
    ? gatewayPermissions.delete
    : true;
  const createPermission = gatewayPermissions
    ? gatewayPermissions.create
    : true;

  const [isGatewayWizardOpen, openGatewayWizard, closeGatewayWizard] =
    useBoolean(false);
  let defaultPage = "general";
  const queryParams = queryString.parse(urlParams);
  if (queryParams && queryParams["page"]) {
    defaultPage = queryParams["page"];
  }
  const {
    gatewayObject,
    isObjectDirty,
    editorPages,
    helpId,
    planObject,
    userObject,
    isExternal,
    isMemberOfCluster,
    availableFirmwares,
    isSMEReportEnabled,
    isReportConfirmationEnabled,
    isHasEditPermissionForReportsConfig,
    hasIpAddress,
    isReachMyDeviceEnabled,
    isPageDataLoaded,
    isInnerPageLoading,
    isPageLoadFailed,
    chosenPageId,
    errorMessagesByPageList,
    onPageChange,
    setPlanObject,
    setUserObject,
    setGatewayData,
    removeGatewayOwner,
    setGatewayOwner,
    saveButtonOnClick,
    revertButtonOnClick,
    reloadPageData,
    deleteButtonOnClick,
    accessGatewayButtonOnClick,
    rebootAction,
    fetchAction,
    sendDailyReportAction,
    sendWeeklyReportAction,
    sendMonthlyReportAction,
    accessGatewayRMDAction,
    accessGatewayIPAction,
    reloadGatewayStatus,
    reloadGatewayCliResults,
    appiData,
    uoTableData,
    zonesData,
    setAppiDataValue,
    setUoTableDataValue,
  } = useGatewayEditor(id, defaultPage);

  // Data passed down to the editor pages
  const editorPageData = {
    gatewayObject,
    setEditorPageId: onPageChange,
    planObject,
    userObject,
    isExternal,
    availableFirmwares,
    isSMEReportEnabled,
    isReportConfirmationEnabled,
    isHasEditPermissionForReportsConfig,
    setPlanObject,
    setUserObject,
    setGatewayData,
    isPageDataLoaded,
    removeGatewayOwner,
    setGatewayOwner,
    reloadGatewayStatus,
    reloadGatewayCliResults,
    modifyPermission,
    appiData,
    uoTableData,
    zonesData,
    setAppiDataValue,
    setUoTableDataValue,
  };

    const name = gatewayObject?.[GatewayGetSchema.id];
    const classes = ButtonsContainerStyles();

    return (
        <>
            <RouteLeavingGuard when={isObjectDirty} />
            <SMCPage loading={!isPageDataLoaded} helpId={helpId}>
                <div className="ObjectEditor" style={{ width: editorWidth }}>
                    <ButtonContainer className={classes.headerContainer}>
                        <div>
                            <LabelContainer value={id}/>
                        </div>
                        <div>
                            <div style={{display: 'flex'}}>
                                <SMCButton
                                    labelClassName={classes.labelColor}
                                    variant="ghost"
                                    disabled={!isPageDataLoaded}
                                    className={classes.labelColor}
                                    onClick={accessGatewayButtonOnClick}
                                >
                                    <img src={seamlessLoginIcon} alt="access gateway" style={{marginRight: '5px'}}/>
                                    {t('SMP.ACCESS_GATEWAY')}
                                </SMCButton>
                                <img src={lineSeparator} className={classes.separator} alt="separator"/>
                                <SMCButton
                                    labelClassName={classes.labelColor}
                                    icon="delete"
                                    variant="ghost"
                                    disabled={!isPageDataLoaded || !deletePermission}
                                    onClick={deleteButtonOnClick}>
                                    {t('SMP_DELETE_GATEWAY')}
                                </SMCButton>
                                <img src={lineSeparator} className={classes.separator} alt="separator"/>
                                <DropdownContainer>
                                    {createPermission && <DropdownRow text="Clone" action={openGatewayWizard}/>}
                                    <DropdownRow text="Reboot" action={rebootAction}/>
                                    <DropdownRow text="Fetch Now" action={fetchAction}/>
                                    <DropdownRow text="Send Daily Report" action={sendDailyReportAction}/>
                                    <DropdownRow text="Send Weekly Report" action={sendWeeklyReportAction}/>
                                    <DropdownRow text="Send Monthly Report" action={sendMonthlyReportAction}/>
                                    {hasIpAddress &&
                                        <DropdownRow text="Access Gateway - IP" action={accessGatewayIPAction}/>}
                                    {isReachMyDeviceEnabled &&
                                        <DropdownRow text="Access Gateway - RMD" action={accessGatewayRMDAction}/>}
                                </DropdownContainer>
                            </div>
                        </div>
                    </ButtonContainer>
                    {/* If user doesn't have modify permission, disable editor */}
                    <SMCDisabledProvider forceDisable={!modifyPermission}>
                        {/* If gateway is a member of cluster, disable editor */}
                        <SMCDisabledProvider forceDisable={isMemberOfCluster}>
                            <SMCEditor
                                editorPageData={editorPageData}
                                height={editorHeight}
                                width={editorWidth}
                                errorListByPage={errorMessagesByPageList}
                                chosenPageId={chosenPageId}
                                onPageChange={onPageChange}
                                isPageLoaded={isPageDataLoaded && !isInnerPageLoading}
                                isPageLoadFailed={isPageLoadFailed}
                                reloadData={reloadPageData}
                                pages={editorPages}
                            />
                        </SMCDisabledProvider>
                    </SMCDisabledProvider>
                </div>
                <GatewayWizard
                    isOpen={isGatewayWizardOpen}
                    onRequestClose={closeGatewayWizard}
                    isClone={true}
                    originalGateway={gatewayObject}
                    originalGatewayId={name}
                    onSuccess={name => navigateTo(SMCPaths.gateways+'?id='+name)}
                />
            </SMCPage>
            <VerifyButtonsContainer
                disabled={!isPageDataLoaded || !modifyPermission}
                onClickSaveButton={saveButtonOnClick}
                onClickCancelButton={revertButtonOnClick}
            />
        </>
    );
}


const mapStateToProps = (state) => ({
    gatewayPermissions: gatewayPermissionsSelector(state),
    urlParams: state.router.location.search
});

const mapDispatchToProps = {
    navigateTo: push
}

export default connect(mapStateToProps, mapDispatchToProps)(GatewayEditor);